import React, { useState } from "react";
import { Button, FormField, Input } from "semantic-ui-react";
import { withTranslation } from "react-i18next";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/pro-solid-svg-icons";

import { VirtualKeyboard, LAYOUTS } from "../../../../components";
import { COMMUNICATION_CHANNELS } from "../../../../util/communication_statuses";
import { PreferredCommunicationChannel } from "./components";
import MainLayout from "../../layout";

import "./index.scss";

const FIELDS = {
  EMAIL: "email",
  PHONE: "phone",
};

const PHONE_PATTERN = /^\+?\d*$/;

const sanitizePhoneNumber = (value = "") => (value.startsWith("+") ? "+" : "") + value.replace(/\D/g, "");

const CustomerInfoScreen = ({
  communication,
  settings,
  customerEmail,
  customerPhoneNumber,
  preferredCommChannel,
  onUpdateCustomerEmail,
  onUpdateCustomerPhoneNumber,
  onSetPreferredCommChannel,
  onGoToNextScreen,
  t,
}) => {
  const [activeField, setActiveField] = useState(null);

  const isPhoneActive = activeField === FIELDS.PHONE;

  const showPreferredCommChannel = Boolean(settings.has_sms || settings.has_whats_app);

  const isNextDisabled =
    (preferredCommChannel === COMMUNICATION_CHANNELS.EMAIL && !customerEmail?.trim()) ||
    ((preferredCommChannel === COMMUNICATION_CHANNELS.SMS || preferredCommChannel === COMMUNICATION_CHANNELS.WHATSAPP) && !customerPhoneNumber?.trim());

  const getCustomerName = () => {
    let fullname = "";

    if (communication.customer) {
      if (communication.customer.title) fullname += communication.customer.title + " ";

      if (communication.customer.firstname) fullname += communication.customer.firstname + " ";

      if (communication.customer.surname) fullname += communication.customer.surname;
    }

    return fullname;
  };

  const handleKeyboardChange = value => {
    if (activeField === FIELDS.EMAIL) onUpdateCustomerEmail(value);
    else if (activeField === FIELDS.PHONE) onUpdateCustomerPhoneNumber(sanitizePhoneNumber(value));
  };

  const handleKeyPress = button => {
    if (button === "{enter}") setActiveField(null);
  };

  const content = (
    <>
      <div className="CustomerInfoScreenModalContainer">
        <div className="CustomerInfoScreenModal">
          <div className="CustomerInfoScreenModal__Header">{t("cc_contact_info_check").message || "Contact Info check"}</div>
          <div className="CustomerInfoScreenModal__Content">
            <div className="customer-name">
              <span>{t("cc_Welcome").message || "Welcome"}</span>
              {getCustomerName()},
            </div>
            <div className="info-message">
              {t("cc_desk_checkin_customer_info_message").message ||
                "Check your contact information below and add or change your email address or phone number if needed."}
            </div>

            <FormField className="input-field">
              <label>{t("cc_email").message || "Email"}</label>
              <Input readOnly fluid value={customerEmail} onFocus={() => setActiveField(FIELDS.EMAIL)} autoComplete="off" />
            </FormField>
            <FormField className="input-field">
              <label>{t("cc_phone_number").message || "Phone Number"}</label>
              <Input readOnly fluid value={customerPhoneNumber} onFocus={() => setActiveField(FIELDS.PHONE)} autoComplete="off" />
            </FormField>
          </div>
        </div>
      </div>

      {showPreferredCommChannel && (
        <PreferredCommunicationChannel settings={settings} preferredCommChannel={preferredCommChannel} onSetPreferredCommChannel={onSetPreferredCommChannel} />
      )}
    </>
  );

  const navigationButtons = (
    <Button className="DeskCheckinNavigationButton" disabled={isNextDisabled} onClick={onGoToNextScreen}>
      <span className="navigation-text">{t("cc_next").message || "NEXT"}</span>
      <FontAwesomeIcon icon={faArrowRight} className="navigation-icon" />
    </Button>
  );

  return (
    <>
      <MainLayout content={content} navigationButtons={navigationButtons} showTitle isCheckingOut={communication.is_checking_out} />
      {activeField && (
        <div className={`CustomerInfoScreenKeyboard ${isPhoneActive ? "phone" : ""}`}>
          <VirtualKeyboard
            key={activeField}
            layout={isPhoneActive ? LAYOUTS.PHONE : LAYOUTS.FULL}
            inputPattern={isPhoneActive ? PHONE_PATTERN : undefined}
            onKeyPress={handleKeyPress}
            onChange={handleKeyboardChange}
            text={isPhoneActive ? sanitizePhoneNumber(customerPhoneNumber) : customerEmail}
          />
        </div>
      )}
    </>
  );
};

export default withTranslation()(CustomerInfoScreen);
